<template>
  <div id="app" class="min-h-screen bg-gray-50 font-arabic" dir="rtl">
    <!-- Navigation - Hide on auth pages -->
    <nav v-if="!isAuthPage" class="bg-white border-b border-gray-200 shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <img class="h-8 w-auto" src="/logo.svg" alt="المساعد القانوني" />
            </div>
            <div class="hidden md:block">
              <div class="ml-10 flex items-baseline space-x-4 space-x-reverse">
                <router-link
                  v-for="item in navigation"
                  :key="item.name"
                  :to="item.path"
                  :class="[
                    'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    $route.name === item.name
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  ]"
                >
                  {{ item.displayName }}
                </router-link>
              </div>
            </div>
            <!-- Mobile menu button -->
            <div class="md:hidden">
              <button
                @click="toggleMobileMenu"
                class="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <Menu class="w-6 h-6" />
              </button>
            </div>
          </div>
          <div class="flex items-center space-x-4 space-x-reverse">
            <!-- Notifications -->
            <router-link 
              v-if="authStore.isAuthenticated && authStore.hasPermission('canAccessNotifications')"
              to="/notifications"
              class="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors relative"
            >
              <Bell class="w-5 h-5" />
              <span 
                v-if="unreadCount > 0"
                class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
              >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
              </span>
            </router-link>
            
            <!-- User Menu -->
            <div class="relative" v-if="authStore.isAuthenticated">
              <button 
                @click="toggleUserMenu"
                class="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <User class="w-5 h-5" />
              </button>
              
              <!-- User Dropdown Menu -->
              <div 
                v-if="showUserMenu"
                id="user-menu"
                class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
              >
                <div class="px-4 py-2 border-b border-gray-100">
                  <p class="text-sm font-medium text-gray-900">{{ authStore.user?.fullName }}</p>
                  <p class="text-xs text-gray-500">{{ authStore.user?.email }}</p>
                  <p class="text-xs text-blue-600">{{ getUserTypeLabel(authStore.user?.userType) }}</p>
                </div>
                
                <router-link
                  to="/profile"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Profile Settings
                </router-link>
                
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
            
            <!-- Login/Register for non-authenticated users -->
            <div v-else class="flex items-center space-x-4 space-x-reverse">
              <router-link
                to="/login"
                class="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Sign In
              </router-link>
              <router-link
                to="/register"
                class="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Register
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Mobile Navigation Menu -->
      <div v-if="showMobileMenu" class="md:hidden bg-white border-t border-gray-200">
        <div class="px-2 pt-2 pb-3 space-y-1">
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="item.path"
            @click="showMobileMenu = false"
            :class="[
              'block px-3 py-2 rounded-md text-base font-medium transition-colors',
              $route.name === item.name
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            ]"
          >
            {{ item.displayName }}
          </router-link>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-1">
      <router-view />
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Bell, User, Menu } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useAuthStore } from './stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const showUserMenu = ref(false)
const showMobileMenu = ref(false)

// Check if current page is an auth page
const isAuthPage = computed(() => {
  const authRoutes = ['Login', 'Register', 'ForgotPassword', 'ResetPassword']
  return authRoutes.includes(router.currentRoute.value.name as string)
})

const navigation = computed(() => {
  if (!authStore.isAuthenticated) {
    return []
  }
  
  // Return all navigation items for authenticated users
  return [
    { name: 'Chat', path: '/', icon: 'MessageCircle', displayName: 'استشارة قانونية' },
    { name: 'Library', path: '/library', icon: 'FileText', displayName: 'المكتبة القانونية' },
    { name: 'Messenger', path: '/messenger', icon: 'MessageSquare', displayName: 'الرسائل' },
    { name: 'Lawyers', path: '/lawyers', icon: 'Users', displayName: 'المحامون' },
    { name: 'FirmOffices', path: '/firm-offices', icon: 'Building', displayName: 'المكاتب القانونية' },
    { name: 'Support', path: '/support', icon: 'Headphones', displayName: 'خدمة العملاء' }
  ]
})

const unreadCount = ref(0)

const getUserTypeLabel = (userType?: string) => {
  const labels: Record<string, string> = {
    client: 'Client',
    user: 'User', 
    lawyer: 'Lawyer',
    firm_admin: 'Firm Administrator'
  }
  return labels[userType || ''] || userType
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const handleLogout = () => {
  authStore.logout()
  showUserMenu.value = false
  router.push('/login')
}

// Close user menu when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const menu = document.getElementById('user-menu')
  if (menu && !menu.contains(target)) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style>
#app {
  font-family: 'Noto Sans Arabic', sans-serif;
}

.router-link-active {
  background-color: rgb(37 99 235);
  color: white;
}
</style>
